import { LOGIN_PAGE } from '@/config/appRoutes';
import {
  buildRedirectTo,
  REDIRECT_TO_PARAM,
  sanitizeRedirectTo,
} from '@/lib/redirectTo';
import {
  ApiResponse,
  AuthResponse,
  RefreshTokenResponse,
  SystemVersions,
  type KycRiskLevel,
  type XmlLogDirection,
  type XmlLogsApiResponse,
} from '@/types';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

/**
 * Centralized API service for handling HTTP requests and JWT authentication.
 *
 * Features:
 * - Automatic JWT token injection
 * - Token refresh on 401 responses
 * - Singleton pattern for consistent state
 * - Type-safe API methods
 * - Automatic logout on refresh failure
 */

class ApiService {
  private readonly api: AxiosInstance;
  private readonly baseURL: string;
  private readonly tokenKey: string;
  private readonly refreshTokenKey: string;
  private readonly tokenExpirationKey: string;
  private readonly refreshTokenExpirationKey: string;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<
    AxiosResponse<ApiResponse<RefreshTokenResponse>>
  > | null = null;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
    this.tokenKey = import.meta.env.VITE_JWT_TOKEN_KEY || 'at';
    this.refreshTokenKey = import.meta.env.VITE_JWT_REFRESH_TOKEN_KEY || 'rt';
    this.tokenExpirationKey = `${this.tokenKey}ea`;
    this.refreshTokenExpirationKey = `${this.refreshTokenKey}ea`;

    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Sets up axios request and response interceptors for token management.
   */
  private setupInterceptors(): void {
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Adds JWT token to request headers and refreshes token proactively if needed.
   */
  private setupRequestInterceptor(): void {
    this.api.interceptors.request.use(
      async config => {
        // Check if we need to refresh the token before the request
        if (this.shouldRefreshTokenBeforeRequest()) {
          try {
            await this.refreshTokenProactively();
          } catch (error) {
            console.error('Proactive token refresh failed:', error);
            // Continue with the request - the response interceptor will handle 401s as fallback
          }
        }

        // Add the current token to the request
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );
  }

  /**
   * Handles token refresh on 401 responses.
   */
  private setupResponseInterceptor(): void {
    this.api.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        if (
          originalRequest &&
          this.shouldAttemptTokenRefresh(error, originalRequest)
        ) {
          return this.handleTokenRefresh(originalRequest, error);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Determines if a token refresh should be attempted.
   */
  private shouldAttemptTokenRefresh(
    error: AxiosError,
    originalRequest: AxiosRequestConfig & { _retry?: boolean }
  ): boolean {
    return (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      this.getRefreshToken() !== null // Only check if refresh token exists, not if expired
    );
  }

  /**
   * Handles the token refresh process and retries the original request.
   */
  private async handleTokenRefresh(
    originalRequest: AxiosRequestConfig & { _retry?: boolean },
    originalError: AxiosError
  ): Promise<AxiosResponse> {
    originalRequest._retry = true;

    // If already refreshing, wait for the current refresh
    if (this.isRefreshing && this.refreshPromise) {
      try {
        await this.refreshPromise;
        return this.retryRequestWithNewToken(originalRequest);
      } catch {
        return Promise.reject(originalError);
      }
    }

    // Start new refresh process
    if (!this.isRefreshing) {
      return this.performTokenRefresh(originalRequest, originalError);
    }

    return Promise.reject(originalError);
  }

  /**
   * Performs the actual token refresh operation.
   */
  private async performTokenRefresh(
    originalRequest: AxiosRequestConfig & { _retry?: boolean },
    originalError: AxiosError
  ): Promise<AxiosResponse> {
    this.isRefreshing = true;

    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      this.refreshPromise = this.refreshTokenRequest(refreshToken);
      const response = await this.refreshPromise;

      this.setToken(
        response.data.result.access_token,
        response.data.result.expires_in
      );

      return this.retryRequestWithNewToken(originalRequest);
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      await this.handleRefreshFailure();
      return Promise.reject(originalError);
    } finally {
      this.resetRefreshState();
    }
  }

  /**
   * Retries the original request with the new token.
   */
  private retryRequestWithNewToken(
    originalRequest: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    const token = this.getToken();
    if (token && originalRequest.headers) {
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return this.api(originalRequest);
    }
    throw new Error('No token available after refresh');
  }

  /**
   * Handles refresh failure by clearing session data and redirecting.
   * Does not call logout endpoint since tokens are invalid.
   */
  private async handleRefreshFailure(): Promise<void> {
    // Don't call logout endpoint since both tokens are invalid - it would fail anyway
    // Instead, directly clean up local session data
    this.removeTokens();

    // Redirect to login with session-expired parameter, preserving the
    // current location so the user returns to it after logging in again
    const loginUrl = new URL(LOGIN_PAGE, window.location.origin);
    loginUrl.searchParams.set('session-expired', 'yes');

    const redirectTo = sanitizeRedirectTo(buildRedirectTo(window.location));
    if (redirectTo) {
      loginUrl.searchParams.set(REDIRECT_TO_PARAM, redirectTo);
    }

    window.location.assign(loginUrl.toString());
  }

  /**
   * Resets the refresh state.
   */
  private resetRefreshState(): void {
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  /**
   * Proactively refreshes the access token when it's about to expire.
   * This method is used by the request interceptor to prevent 401 errors.
   * It reuses the existing refresh logic to maintain consistency.
   */
  private async refreshTokenProactively(): Promise<void> {
    // If already refreshing, wait for the current refresh
    if (this.isRefreshing && this.refreshPromise) {
      try {
        await this.refreshPromise;
        return;
      } catch (error) {
        console.error('Waiting for ongoing refresh failed:', error);
        throw error;
      }
    }

    // Start new refresh process if not already refreshing
    if (!this.isRefreshing) {
      try {
        this.isRefreshing = true;

        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available for proactive refresh');
        }

        this.refreshPromise = this.refreshTokenRequest(refreshToken);
        const response = await this.refreshPromise;

        this.setToken(
          response.data.result.access_token,
          response.data.result.expires_in
        );
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
        // Don't handle refresh failure here - let the response interceptor handle it
        throw error;
      } finally {
        this.resetRefreshState();
      }
    }
  }

  // Token management
  /**
   * Sets a token in localStorage with optional expiration.
   */
  private setTokenWithExpiration(
    tokenKey: string,
    expirationKey: string,
    token: string,
    expiresInSeconds?: number
  ): void {
    localStorage.setItem(tokenKey, token);
    if (expiresInSeconds) {
      const expirationDate = new Date(Date.now() + expiresInSeconds * 1000);
      localStorage.setItem(expirationKey, expirationDate.toISOString());
    }
  }

  /**
   * Gets token expiration date from localStorage.
   */
  private getTokenExpirationDate(expirationKey: string): Date | null {
    const expiration = localStorage.getItem(expirationKey);
    return expiration ? new Date(expiration) : null;
  }

  /**
   * Checks if a token is expired based on its expiration key.
   */
  private isTokenExpiredByKey(expirationKey: string): boolean {
    const expiration = this.getTokenExpirationDate(expirationKey);
    if (!expiration) return false; // If no expiration date, assume token is valid
    return new Date() >= expiration;
  }

  /**
   * Sets the access token and its expiration.
   */
  public setToken(token: string, expiresInSeconds?: number): void {
    this.setTokenWithExpiration(
      this.tokenKey,
      this.tokenExpirationKey,
      token,
      expiresInSeconds
    );
  }

  /**
   * Gets the current access token.
   */
  public getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Gets the access token expiration date.
   */
  public getTokenExpiration(): Date | null {
    return this.getTokenExpirationDate(this.tokenExpirationKey);
  }

  /**
   * Sets the refresh token and its expiration.
   */
  public setRefreshToken(token: string, expiresInSeconds?: number): void {
    this.setTokenWithExpiration(
      this.refreshTokenKey,
      this.refreshTokenExpirationKey,
      token,
      expiresInSeconds
    );
  }

  /**
   * Gets the current refresh token.
   */
  public getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Gets the refresh token expiration date.
   */
  public getRefreshTokenExpiration(): Date | null {
    return this.getTokenExpirationDate(this.refreshTokenExpirationKey);
  }

  /**
   * Removes all tokens from localStorage.
   */
  public removeTokens(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.tokenExpirationKey);
    localStorage.removeItem(this.refreshTokenExpirationKey);
  }

  // Auth methods
  /**
   * Authenticates a user with email and password.
   * Automatically stores tokens in localStorage upon successful login.
   *
   * @param email - User's email address
   * @param password - User's password
   * @returns API response containing access token, refresh token, and user data
   * @throws {AxiosError} If authentication fails (invalid credentials, server error, etc.)
   */
  public async login(
    email: string,
    password: string
  ): Promise<AxiosResponse<ApiResponse<AuthResponse>>> {
    const response = await this.api.post<ApiResponse<AuthResponse>>(
      '/auth/login',
      { email, password }
    );

    const { result } = response.data;
    if (result?.access_token) {
      this.setToken(result.access_token, result.expires_in);

      if (result.refresh_token) {
        this.setRefreshToken(result.refresh_token, result.refresh_expires_in);
      }
    }

    return response;
  }

  /**
   * Registers a new user account.
   *
   * @param userData - User registration data including name, email, and password
   * @returns API response containing the new user's authentication tokens and profile
   * @throws {AxiosError} If registration fails (validation errors, duplicate email, server error, etc.)
   */
  public async register(userData: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
  }): Promise<AxiosResponse<ApiResponse<AuthResponse>>> {
    return this.api.post<ApiResponse<AuthResponse>>('/auth/register', userData);
  }

  /**
   * Logs out the current user and cleans up tokens from localStorage.
   * Attempts to notify the backend but ensures local cleanup happens regardless.
   * This prevents orphaned sessions if the logout API call fails.
   *
   * @throws Errors are caught and logged, never thrown to caller
   */
  public async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        await this.api.post('/auth/logout', { refresh_token: refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.resetRefreshState();
      this.removeTokens();
    }
  }

  /**
   * Refreshes the access token using the refresh token.
   * Public method that uses the interceptor-enabled axios instance.
   * Typically used by the response interceptor when a 401 error occurs.
   *
   * @param refreshToken - The refresh token to use for obtaining a new access token
   * @returns API response containing the new access token
   * @throws {AxiosError} If refresh fails (invalid refresh token, server error, etc.)
   */
  public async refreshToken(
    refreshToken: string
  ): Promise<AxiosResponse<ApiResponse<RefreshTokenResponse>>> {
    return this.api.post<ApiResponse<RefreshTokenResponse>>('/auth/refresh', {
      refresh_token: refreshToken,
    });
  }

  /**
   * Private method for refresh token requests that bypasses interceptors.
   * This prevents infinite loops during token refresh.
   */
  private async refreshTokenRequest(
    refreshToken: string
  ): Promise<AxiosResponse<ApiResponse<RefreshTokenResponse>>> {
    const refreshApi = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    return refreshApi.post<ApiResponse<RefreshTokenResponse>>('/auth/refresh', {
      refresh_token: refreshToken,
    });
  }

  /**
   * Gets the current authenticated user information from the backend.
   * Includes user profile, role, permissions, and account status.
   *
   * @returns API response containing the authenticated user's data
   * @throws {AxiosError} If the request fails or user is not authenticated
   */
  public async getCurrentUser(): Promise<AxiosResponse> {
    return this.api.get('/auth/me');
  }

  /**
   * Gets version and build metadata for the backend and Nova Forms frontend.
   * Used to display application version information in the UI for debugging and verification.
   *
   * @returns API response containing version info for backend and forms applications
   * @throws {AxiosError} If the request fails
   */
  public async getSystemVersions(): Promise<
    AxiosResponse<ApiResponse<SystemVersions>>
  > {
    return this.api.get('/version');
  }

  /**
   * Gets KYC approval data for a specific customer.
   * Includes customer information, KYC updates, document groups, and approval history.
   *
   * @param customerId - The ID of the customer
   * @returns API response containing the customer's KYC approval data
   * @throws {AxiosError} If the request fails or customer is not found
   */
  public async getKycApprovalData(customerId: number): Promise<AxiosResponse> {
    return this.api.get(`/kyc/approval-data/${customerId}`);
  }

  public async addKycDocumentGroupComment(
    documentGroupId: number,
    comment: string
  ): Promise<AxiosResponse> {
    return this.api.post(
      `/kyc/update-document-groups/${documentGroupId}/comments`,
      {
        comment,
      }
    );
  }

  /**
   * Updates the risk level for a KYC update record.
   * Risk levels are: LOW (1), MEDIUM (2), HIGH (3).
   *
   * @param payload - Object containing the KYC update ID and new risk level
   * @returns API response confirming the risk level update
   * @throws {AxiosError} If the update fails or KYC update is not found
   */
  public async updateKycRiskLevel(payload: {
    kyc_update_id: number;
    risk_level: KycRiskLevel;
  }): Promise<AxiosResponse> {
    return this.api.post('/kyc/update-risk-level', payload);
  }

  /**
   * Downloads the compliance PDF document for a KYC record.
   * Response type is 'blob' for file download handling.
   *
   * @param kycId - The ID of the KYC record
   * @returns API response with file blob data
   * @throws {AxiosError} If the download fails or KYC record is not found
   */
  public async downloadKycComplianceFile(
    kycId: number
  ): Promise<AxiosResponse> {
    return this.api.get(`/kyc/compliance-file/${kycId}`, {
      responseType: 'blob',
    });
  }

  public async downloadHistoricalComplianceDocument(
    historyId: number
  ): Promise<AxiosResponse> {
    return this.api.get(
      `/kyc/compliance-document-history/${historyId}/download`,
      {
        responseType: 'blob',
      }
    );
  }

  public async downloadHistoricalDocumentGroupFile(
    fileId: number
  ): Promise<AxiosResponse> {
    return this.api.get(`/kyc/update-document-group-files/${fileId}/download`, {
      responseType: 'blob',
    });
  }

  public async downloadHistoricalAttachmentFile(
    fileId: number
  ): Promise<AxiosResponse> {
    return this.api.get(`/kyc/historical-attachment-files/${fileId}/download`, {
      responseType: 'blob',
    });
  }

  // ==================== XML Logs Methods ====================

  /**
   * Fetch a paginated list of XML import logs.
   *
   * @param params - Query params (page, per_page, status, from_date, to_date)
   */
  public async getXmlImports(
    params?: Record<string, string | number | undefined>
  ): Promise<AxiosResponse<XmlLogsApiResponse>> {
    return this.api.get<XmlLogsApiResponse>('/logs/xml/imports', { params });
  }

  /**
   * Fetch a paginated list of XML export logs.
   *
   * @param params - Query params (page, per_page, status, from_date, to_date)
   */
  public async getXmlExports(
    params?: Record<string, string | number | undefined>
  ): Promise<AxiosResponse<XmlLogsApiResponse>> {
    return this.api.get<XmlLogsApiResponse>('/logs/xml/exports', { params });
  }

  /**
   * Download the XML file for an import or export log record.
   * Returns the file contents as a blob.
   *
   * @param direction - 'imports' or 'exports'
   * @param id - The log record id
   */
  public async downloadXmlLog(
    direction: XmlLogDirection,
    id: number
  ): Promise<AxiosResponse> {
    return this.api.get(`/logs/xml/${direction}/${id}/download`, {
      responseType: 'blob',
    });
  }

  public async generateKycComplianceDocument(
    kycId: number
  ): Promise<AxiosResponse> {
    return this.api.post(`/kyc/generate-compliance-document/${kycId}`);
  }

  /**
   * Sends a compliance email for a KYC record.
   * Supports customizable subject, body, recipients, CC, BCC, and file attachments.
   * Files are referenced by their IDs, which must exist in the system prior to sending.
   *
   * @param payload - Email configuration including KYC ID, subject, body, and optional recipient lists
   * @returns API response confirming the email was sent
   * @throws {AxiosError} If email sending fails (invalid recipients, missing files, server error, etc.)
   */
  public async sendKycComplianceEmail(payload: {
    kyc_id: number;
    subject: string;
    body: string;
    recipients?: string;
    cc?: string;
    bcc?: string;
    uploaded_file_ids?: number[];
  }): Promise<AxiosResponse> {
    return this.api.post('/kyc/send-compliance-email', payload);
  }

  public async generateKycAdminEditToken(
    customerId: number
  ): Promise<AxiosResponse> {
    return this.api.post('/kyc/admin-edit-token', { customer_id: customerId });
  }

  /**
   * Starts a KYC renewal process for a customer.
   * Changes the KYC status to RENEWAL and generates an XML export for the customer.
   * The export can be used to import the KYC data into external systems.
   *
   * @param customerId - The ID of the customer to renew KYC for
   * @returns API response confirming the renewal process has started
   * @throws {AxiosError} If the renewal fails (customer not found, already in renewal, etc.)
   */
  public async startKycRenewal(customerId: number): Promise<AxiosResponse> {
    return this.api.post(`/kyc/start-renewal/${customerId}`);
  }

  /**
   * Resend the KYC form email to a customer.
   * Invalidates the previously sent token and sends a new one with an extended expiration date.
   */
  public async resendKycEmail(customerId: number): Promise<AxiosResponse> {
    return this.api.post(`/kyc/resend-email/${customerId}`);
  }

  public async downloadDocumentGroupFile(
    documentGroupFileId: number
  ): Promise<AxiosResponse> {
    return this.api.get(
      `/kyc/update-document-group-files/${documentGroupFileId}/download`,
      {
        responseType: 'blob',
      }
    );
  }

  public async deleteDocumentGroupFile(
    documentGroupFileId: number
  ): Promise<AxiosResponse> {
    return this.api.delete(
      `/kyc/update-document-group-files/${documentGroupFileId}`
    );
  }

  /**
   * Helper method for document file approval/rejection actions.
   * Reduces code duplication by centralizing the pattern.
   */
  private async approveRejectDocumentFile(
    fileId: number,
    level: 1 | 2,
    action: 'approve' | 'reject'
  ): Promise<AxiosResponse> {
    return this.api.post(
      `/kyc/update-document-group-files/${fileId}/approve-level-${level}`,
      { action }
    );
  }

  /**
   * Approve a document group file at level 1 of the approval workflow.
   * Part of the multi-level document approval process in KYC management.
   *
   * @param documentGroupFileId - The ID of the document group file to approve
   * @returns API response confirming level 1 approval
   * @throws {AxiosError} If approval fails (file not found, unauthorized, etc.)
   */
  public async approveDocumentGroupFileLevel1(
    documentGroupFileId: number
  ): Promise<AxiosResponse> {
    return this.approveRejectDocumentFile(documentGroupFileId, 1, 'approve');
  }

  /**
   * Reject a document group file at level 1.
   */
  public async rejectDocumentGroupFileLevel1(
    documentGroupFileId: number
  ): Promise<AxiosResponse> {
    return this.approveRejectDocumentFile(documentGroupFileId, 1, 'reject');
  }

  /**
   * Approve a document group file at level 2.
   */
  public async approveDocumentGroupFileLevel2(
    documentGroupFileId: number
  ): Promise<AxiosResponse> {
    return this.approveRejectDocumentFile(documentGroupFileId, 2, 'approve');
  }

  /**
   * Reject a document group file at level 2.
   */
  public async rejectDocumentGroupFileLevel2(
    documentGroupFileId: number
  ): Promise<AxiosResponse> {
    return this.approveRejectDocumentFile(documentGroupFileId, 2, 'reject');
  }

  /**
   * Uploads attachment files to a KYC record during the approval process.
   * Files are sent as multipart form data. Multiple files can be uploaded in one request.
   *
   * @param customerId - The ID of the customer whose KYC is being processed
   * @param files - Array of File objects to upload
   * @returns API response containing the uploaded file IDs
   * @throws {AxiosError} If upload fails (invalid files, quota exceeded, customer not found, etc.)
   */
  public async uploadKycAttachmentFiles(
    customerId: number,
    files: File[]
  ): Promise<AxiosResponse> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files[]', file);
    });

    return this.api.post(`/kyc/attachment-files/${customerId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  /**
   * Helper method for attachment file downloads.
   * Centralizes endpoint pattern to reduce duplication.
   */
  private downloadKycAttachmentFileByEndpoint(
    customerId: number,
    fileId: number,
    endpoint: 'approval-download' | 'download'
  ): Promise<AxiosResponse> {
    return this.api.get(
      `/kyc/attachment-files/${customerId}/${fileId}/${endpoint}`,
      { responseType: 'blob' }
    );
  }

  /**
   * Download an attachment file from a KYC record in approval
   */
  public async downloadKycAttachmentFileApproval(
    customerId: number,
    fileId: number
  ): Promise<AxiosResponse> {
    return this.downloadKycAttachmentFileByEndpoint(
      customerId,
      fileId,
      'approval-download'
    );
  }

  /**
   * Download an attachment file from an approved KYC record
   */
  public async downloadKycAttachmentFile(
    customerId: number,
    fileId: number
  ): Promise<AxiosResponse> {
    return this.downloadKycAttachmentFileByEndpoint(
      customerId,
      fileId,
      'download'
    );
  }

  /**
   * Delete an attachment file from a KYC record
   */
  public async deleteKycAttachmentFile(
    customerId: number,
    fileId: number
  ): Promise<AxiosResponse> {
    return this.api.delete(`/kyc/attachment-files/${customerId}/${fileId}`);
  }

  // ==================== Generic API Methods ====================
  // These methods provide low-level access to HTTP verbs.
  // Prefer using specialized methods above when available.

  /**
   * Performs a GET request.
   * Automatically includes JWT token in Authorization header.
   * Proactively refreshes token if needed before making the request.
   *
   * @param url - The API endpoint URL (relative to baseURL)
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  public async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  /**
   * Performs a POST request.
   * Automatically includes JWT token in Authorization header.
   * Proactively refreshes token if needed before making the request.
   *
   * @param url - The API endpoint URL (relative to baseURL)
   * @param data - Request body data
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  /**
   * Performs a PUT request.
   * Automatically includes JWT token in Authorization header.
   * Proactively refreshes token if needed before making the request.
   *
   * @param url - The API endpoint URL (relative to baseURL)
   * @param data - Request body data
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  /**
   * Performs a PATCH request.
   * Automatically includes JWT token in Authorization header.
   * Proactively refreshes token if needed before making the request.
   *
   * @param url - The API endpoint URL (relative to baseURL)
   * @param data - Request body data
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  public async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, config);
  }

  /**
   * Performs a DELETE request.
   * Automatically includes JWT token in Authorization header.
   * Proactively refreshes token if needed before making the request.
   *
   * @param url - The API endpoint URL (relative to baseURL)
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  public async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }

  // ==================== Authentication State Methods ====================
  // Check authentication status and token expiration states

  /**
   * Checks if the user is currently authenticated.
   * Returns true only if a valid token exists and the refresh token is not expired.
   * This is the recommended method to check if a user is still authenticated.
   *
   * @returns true if the user has valid tokens, false otherwise
   */
  public isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check if refresh token is expired (if no refresh token, check access token)
    return !this.isRefreshTokenExpired();
  }

  /**
   * Checks if the access token is expired based on stored expiration time.
   *
   * @returns true if the access token has expired, false otherwise
   */
  public isTokenExpired(): boolean {
    return this.isTokenExpiredByKey(this.tokenExpirationKey);
  }

  /**
   * Checks if the refresh token is expired based on stored expiration time.
   *
   * @returns true if the refresh token has expired, false otherwise
   */
  public isRefreshTokenExpired(): boolean {
    return this.isTokenExpiredByKey(this.refreshTokenExpirationKey);
  }

  /**
   * Checks if the access token will expire within the specified buffer time.
   * Useful for proactive token refresh to prevent user interruption.
   *
   * @param bufferSeconds - Number of seconds before expiration to consider as "about to expire" (default: 30)
   * @returns true if token will expire within the buffer time
   */
  public isTokenAboutToExpire(bufferSeconds: number = 30): boolean {
    const expiration = this.getTokenExpirationDate(this.tokenExpirationKey);
    if (!expiration) return false; // If no expiration date, assume token is valid

    const bufferTime = new Date(Date.now() + bufferSeconds * 1000);
    return bufferTime >= expiration;
  }

  /**
   * Checks if the token needs to be refreshed before making a request.
   * Considers both expiration and buffer time, and validates that refresh token exists.
   * This is used internally by the request interceptor for proactive refresh.
   *
   * @param bufferSeconds - Number of seconds before expiration to consider as "needs refresh" (default: 30)
   * @returns true if token should be refreshed before making requests
   */
  public shouldRefreshTokenBeforeRequest(bufferSeconds: number = 30): boolean {
    const token = this.getToken();
    if (!token) return false; // No token, no need to refresh

    const refreshToken = this.getRefreshToken();
    if (!refreshToken || this.isRefreshTokenExpired()) {
      return false; // Can't refresh without valid refresh token
    }

    return this.isTokenExpired() || this.isTokenAboutToExpire(bufferSeconds);
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
